import { useEffect } from 'react';

import values from 'lodash/values';

import { MeaningfulPaintIntervalType } from '@watershed/constants/monitoring';
import { Teams } from '@watershed/constants/teams';
import { CustomSpanAttribute } from '@watershed/shared-universal/constants/otelCustomAttributes';

import usePaintEffect from '../../hooks/usePaintEffect';
import { perfume } from './initializeFrontendPerformanceMonitoring';

export const MEANINGFUL_PAINT_PAGE_NAME = {
  SUPPLY_CHAIN_OVERVIEW: 'supplyChain.SuppliersOverview',
  SUPPLY_CHAIN_TASKS: 'supplyChain.Tasks',
  SUPPLY_CHAIN_SUPPLIER_DETAILS: 'supplyChain.supplierDetails',

  // Datasets and Objects
  DRILLDOWN: 'drilldown', // This one measures strictly the time to render the BiData response for the table
  DRILLDOWN_OVERVIEW: 'drilldown.overview',
  DRILLDOWN_PAGE: 'drilldown.page',
  ACTIVITY_DATA_LANDING: 'activityData.landing',
  ACTIVITY_DATA_PAGE: 'activityData.page',
  VALUE_MAPPING: 'valueMapping.landing',

  // Data Ingestion
  DI_DATASOURCE_TASK_PAGE: 'dataIngestion.datasourceTaskPage',
  DI_MEASUREMENT_PROJECTS_PAGE: 'dataIngestion.measurementProjectsPage',
  DI_MEASUREMENT_PROJECT_PAGE: 'dataIngestion.measurementProjectPage',
  // Tab (Facilities) of DI_MEASUREMENT_PROJECT_PAGE
  DI_MEASUREMENT_FACILITIES_TAB: 'dataIngestion.measurementFacilitiesTab',
  DI_ASSIGN_FACILITIES_DIALOG: 'dataIngestion.assignFacilitiesDialog',
  DI_SINGLE_FACILITY_PAGE: 'dataIngestion.singleFacilityPage',

  // Ent found
  ORG_STRUCTURE_DETAILS_PAGE: 'orgStructure.detailsPage',
  ORG_STRUCTURE_DETAILS_EXPLORE_RELATIONSHIPS:
    'orgStructure.detailsPage.exploreRelationships',
} as const;

export const MEANINGFUL_PAINT_PAGE_NAMES: Set<string> = new Set(
  values(MEANINGFUL_PAINT_PAGE_NAME)
);

export type MeaningfulPaintPageName =
  (typeof MEANINGFUL_PAINT_PAGE_NAME)[keyof typeof MEANINGFUL_PAINT_PAGE_NAME];

export type MeaningfulPaintTimerProps = {
  owner: Teams;
  pageName: MeaningfulPaintPageName;
  /**
   * If provided, will be recorded as custom attribute WEB_VITALS_MP_INTERVAL
   */
  intervalType?: MeaningfulPaintIntervalType;
  extraAttributes?: Partial<Record<CustomSpanAttribute, string | number>>;
};

export type MeaningfulPaintAdditionalProperties = Partial<
  Record<CustomSpanAttribute, string | number>
>;

export type StartMeaningfulPaintTimerParams = {
  /**
   * Optional interval type to measure. If not provided, there can only be
   * one global timer per page.
   */
  intervalType?: MeaningfulPaintIntervalType;
  owner: Teams;
  pageName: MeaningfulPaintPageName;
  restartTimer?: boolean;
};

type MaybeStartMeaningfulPaintTimerParams = Omit<
  StartMeaningfulPaintTimerParams,
  'pageName'
> & {
  isReadyToStart?: boolean;
  pageName: MeaningfulPaintPageName | undefined;
};

export type StopMeaningfulPaintTimerParams = {
  additionalProperties?: MeaningfulPaintAdditionalProperties;
  /**
   * Optional interval type to measure. If not provided, there can only be
   * one global timer per page.
   */
  intervalType?: MeaningfulPaintIntervalType;
  pageName: MeaningfulPaintPageName;
};

/**
 * MaybeStop... differs from Stop... in that a null pageName will do nothing
 * and isFinishedRendering being false will do nothing.
 */
type MaybeStopMeaningfulPaintTimerParams = Omit<
  StopMeaningfulPaintTimerParams,
  'pageName'
> & {
  isFinishedRendering?: boolean;
  pageName: MeaningfulPaintPageName | undefined;
};

function getTimerKey(
  pageName: MeaningfulPaintPageName,
  intervalType?: MeaningfulPaintIntervalType
) {
  if (!intervalType) {
    return pageName;
  }
  return `${pageName}.${intervalType}`;
}

/*
 * A global set of running timer names to track properties of each timer
 */
const globalTimers = new Map<string, MeaningfulPaintTimerProps>();

/**
 * Used to clear timers if we're resetting some page state
 * - while changing pages or tabs
 * - without fully refreshing: ex. redirecting to different steps in a flow.
 *
 * NOTE: this is a bit over-agressive to try to avoid bloated data, but if we
 * cleared a timer, we shouldn't be tracking it/stopping it at that point.
 */
export function clearAllMeaningfulPaintTimers() {
  // Clear Perfume timers
  globalTimers.forEach((timer) => {
    const timerKey = getTimerKey(timer.pageName, timer.intervalType);
    perfume?.clear(timerKey);
  });
  // Then clear our map that stored them
  globalTimers.clear();
}

/**
 * We throw away timers if something interrupts the page load. This prevents
 * bloating of our data and accidentally tracking cross-session durations.
 *
 * This prevents for example, a user entering page, starting timer, pressing
 * back, coming into the page again and having the timer count that whole period.
 *
 * Actions that trigger removing timers:
 * - Leaves tab
 * - Closes tab
 * - Navigate out of app (to new tab)
 * - Refresh page
 * - Press back button/navigate keyboard shortcut back
 */
function handleInterruptions() {
  // Handle user leaving/switch tab
  window.addEventListener('blur', () => {
    clearAllMeaningfulPaintTimers();
  });

  // Handle user closing tab, refreshing page, or moving outside of app
  window.addEventListener('beforeunload', () => {
    clearAllMeaningfulPaintTimers();
  });

  // If user presses back button or keyboard shortcuts back, remove timers
  window.addEventListener('popstate', () => {
    clearAllMeaningfulPaintTimers();
  });
}

/**
 * Starts a timer for a latency measurement.
 *
 * Only one timer can be running for a given pageName at a time, if a 2nd
 * timer for the same pageName is started, it will be ignored.
 *
 * @param params Object containing pageName, owner, and optional restartTimer flag
 */
export function startMeaningfulPaintTimer({
  intervalType,
  pageName,
  owner,
  restartTimer = false,
}: StartMeaningfulPaintTimerParams) {
  const timerKey = getTimerKey(pageName, intervalType);

  // If there's already a timer running for this event, do nothing.
  if (globalTimers.has(timerKey)) {
    if (!restartTimer) {
      return;
    }
    globalTimers.delete(timerKey);
    perfume?.clear(timerKey);
  }

  // To not inflate our timing, we need to handle user leaving load incomplete
  handleInterruptions();

  globalTimers.set(timerKey, {
    owner,
    pageName,
    ...(intervalType && { intervalType }),
  });
  perfume?.start(timerKey);
}

/**
 * startMeaningfulPaintTimer variation with conditional controls:
 *
 * isReadyToStart: Offers conditional to control when to start the timer or not.
 *   If you want to start timer unconditionally immediately, use useStartMeaningfulPaintTimer.
 * pageName: Optional, skips starting timer if not provided. This allows us
 *   to have logging in deeply nested components that are reused elsewhere
 *   from different parents with different page logging.
 */
export function maybeStartMeaningfulPaintTimer({
  pageName,
  owner,
  restartTimer,
  intervalType,
  isReadyToStart = true,
}: MaybeStartMeaningfulPaintTimerParams) {
  if (!isReadyToStart || !pageName) {
    return;
  }
  startMeaningfulPaintTimer({
    pageName,
    owner,
    restartTimer,
    intervalType,
  });
}

/**
 * useStartMeaningfulPaintTimer variation with conditional controls:
 *
 * isReadyToStart: Offers conditional to control when to start the timer or not.
 *   If you want to start timer unconditionally immediately, use useStartMeaningfulPaintTimer.
 * pageName: Optional, skips starting timer if not provided. This allows us
 *   to have logging in deeply nested components that are reused elsewhere
 *   from different parents with different page logging.
 */
export const useMaybeStartMeaningfulPaintTimer = ({
  pageName,
  owner,
  restartTimer,
  intervalType,
  isReadyToStart = true,
}: MaybeStartMeaningfulPaintTimerParams) => {
  useEffect(() => {
    maybeStartMeaningfulPaintTimer({
      pageName,
      owner,
      restartTimer,
      intervalType,
      isReadyToStart,
    });
  }, [pageName, owner, restartTimer, intervalType, isReadyToStart]);
};

export const useStartMeaningfulPaintTimer = ({
  pageName,
  owner,
  restartTimer,
  intervalType,
}: StartMeaningfulPaintTimerParams) => {
  useMaybeStartMeaningfulPaintTimer({
    pageName,
    owner,
    restartTimer,
    intervalType,
    isReadyToStart: true,
  });
};

/**
 * Stops a timer for a latency measurement, and reports the measurement to
 * Honeycomb.
 *
 * If the timer for the given eventName is not running, this function will
 * do nothing. This is intentional behaviour, with events that result in
 * UI changes in a very different part of the app (perhaps they open a modal
 * or trigger navigation) it's easier to just optimistically stop the timer
 * rather than having UI-level code keep track of whether it's running or not.
 *
 * @param params Object containing pageName and optional additionalProperties
 */
export function stopMeaningfulPaintTimerAndReportToHoneycomb({
  pageName,
  additionalProperties = {},
  intervalType,
}: StopMeaningfulPaintTimerParams) {
  const timerKey = getTimerKey(pageName, intervalType);
  const timer = globalTimers.get(timerKey);

  // If there's no timer running for this event, do nothing.
  if (!timer) {
    return;
  }

  globalTimers.delete(timerKey);
  perfume?.end(timerKey, {
    owner: timer.owner,
    pageName,
    intervalType,
    ...additionalProperties,
  });
}

export function maybeStopMeaningfulPaintTimerAndReportToHoneycomb({
  pageName,
  isFinishedRendering = true,
  additionalProperties,
  intervalType,
}: MaybeStopMeaningfulPaintTimerParams) {
  if (!isFinishedRendering || !pageName) {
    return;
  }
  stopMeaningfulPaintTimerAndReportToHoneycomb({
    pageName,
    additionalProperties,
    intervalType,
  });
}

export const useMaybeStopMeaningfulPaintTimerAndReportToHoneycomb = ({
  pageName,
  isFinishedRendering = true,
  additionalProperties,
  intervalType,
}: MaybeStopMeaningfulPaintTimerParams) => {
  usePaintEffect(() => {
    maybeStopMeaningfulPaintTimerAndReportToHoneycomb({
      pageName,
      additionalProperties,
      intervalType,
      isFinishedRendering,
    });
  });
};

export const useStopMeaningfulPaintTimerAndReportToHoneycomb = ({
  pageName,
  isFinishedRendering = true,
  additionalProperties,
  intervalType,
}: StopMeaningfulPaintTimerParams & { isFinishedRendering?: boolean }) => {
  useMaybeStopMeaningfulPaintTimerAndReportToHoneycomb({
    pageName,
    isFinishedRendering,
    additionalProperties,
    intervalType,
  });
};
